import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Paper,
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";

import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import ClienteDadosGerais from "./components/ClienteDadosGerais";
import ClienteEnderecos from "./components/ClienteEnderecos";
import ClienteContatos from "./components/ClienteContatos";
import ClienteHistorico from "./components/ClienteHistorico";
import ClienteNegociacoes from "./components/ClienteNegociacoes";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { requestFollowUpPrompt } from "../../helpers/followUpPrompt";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";
import { splitBrazilianPhone } from "../../utils/phoneFormat";

const CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY = "clienteFieldRequirementsV1";
const DEFAULT_FIELD_REQUIREMENTS = {
  cliente: {
    NOMECLI: true,
    NOMEFANTASIA: false,
    PESSOA: true,
    STATUS: false,
    USUARIO_RESPONSAVEL: false,
    INSCESTCLI: false,
    insc_municipal: false,
    suframa: false,
    contribuinte_icms: false,
    DDDCLI: false,
    E_MAIL: false,
    FONECLI: false,
    DDDCELULAR: false,
    CELULAR: false,
    site: false,
    CEPCLI: false,
    ENDCLI: false,
    NUMERO: false,
    BAIRROCLI: false,
    CIDADECLI: false,
    ESTADOCLI: false,
    LIMITE: false,
    metas_vendas: false,
    potencial_vendas: false,
    acoes_vendas: false,
    OBS: false,
    CGCCLI: false,
    CPFCLI: false,
  },
  contato: {
    nome: true,
    email: false,
    celular: false,
    cargo: false,
  },
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cliente-tabpanel-${index}`}
      aria-labelledby={`cliente-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

const ClienteDetalhes = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { clienteId } = useParams();
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useSettings();

  const [loading, setLoading] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [fieldRequirements, setFieldRequirements] = useState(
    DEFAULT_FIELD_REQUIREMENTS
  );
  const [clienteData, setClienteData] = useState({
    NOMECLI: "",
    NOMEFANTASIA: "",
    CGCCLI: "",
    CPFCLI: "",
    PESSOA: "J",
    FONECLI: "",
    DDDCLI: "",
    CELULAR: "",
    DDDCELULAR: "",
    E_MAIL: "",
    ENDCLI: "",
    NUMERO: "",
    CIDADECLI: "",
    ESTADOCLI: "",
    CEPCLI: "",
    BAIRROCLI: "",
    INSCESTCLI: "",
    insc_municipal: "",
    contribuinte_icms: "Não",
    suframa: "",
    segmento: "",
    site: "",
    LIMITE: 0,
    STATUS: "A",
    OBS: "",
  });
  const [preselectedContactId, setPreselectedContactId] = useState(null);

  // Estrangeiro não tem DDD: devolve vazio em vez de fatiar o número como se
  // fosse brasileiro (antes "14155552671" virava DDD "14" + celular "155552671").
  const parseContactPhone = (rawNumber) => splitBrazilianPhone(rawNumber);

  useEffect(() => {
    if (clienteId && clienteId !== "novo") {
      fetchCliente();
    }
  }, [clienteId]);

  useEffect(() => {
    const loadFieldRequirements = async () => {
      try {
        const settings = await getAllSettings();
        if (!Array.isArray(settings)) {
          return;
        }

        const setting = settings.find(
          (item) => item.key === CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY
        );

        if (!setting?.value) {
          return;
        }

        const parsed = JSON.parse(setting.value);
        setFieldRequirements((prevState) => ({
          cliente: {
            ...prevState.cliente,
            ...(parsed?.cliente || {}),
          },
          contato: {
            ...prevState.contato,
            ...(parsed?.clienteContato || parsed?.contato || {}),
          },
        }));
      } catch (error) {
        console.error("Erro ao carregar configuração de campos obrigatórios:", error);
      }
    };

    loadFieldRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clienteId !== "novo" || !user?.id) {
      return;
    }

    setClienteData((prevState) => {
      if (prevState.USUARIO_RESPONSAVEL) {
        return prevState;
      }

      return {
        ...prevState,
        USUARIO_RESPONSAVEL: Number(user.id),
      };
    });
  }, [clienteId, user?.id]);

  useEffect(() => {
    if (clienteId !== "novo") {
      return;
    }

    const preselectedContact = location.state?.preselectedContact;
    if (!preselectedContact?.id) {
      return;
    }

    const phoneData = parseContactPhone(preselectedContact.number);
    setPreselectedContactId(Number(preselectedContact.id));

    // Autor: Copilot | Data: 2026-04-20 | Motivo: pré-preencher novo cliente com dados do contato selecionado
    setClienteData((prevState) => ({
      ...prevState,
      NOMECLI: prevState.NOMECLI || preselectedContact.name || "",
      E_MAIL: prevState.E_MAIL || preselectedContact.email || "",
      DDDCELULAR: prevState.DDDCELULAR || phoneData.DDDCELULAR,
      CELULAR: prevState.CELULAR || phoneData.CELULAR
    }));
  }, [clienteId, location.state]);

  const fetchCliente = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/clientes/${clienteId}`);
      setCliente(data);
      setClienteData(data);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSaveCliente = async () => {
    // Previne cliques múltiplos durante o salvamento
    if (loading) return;

    const clienteFieldLabels = {
      NOMECLI: "Razão Social / Nome",
      NOMEFANTASIA: "Nome Fantasia",
      PESSOA: "Tipo de Pessoa",
      STATUS: "Status",
      USUARIO_RESPONSAVEL: "Usuário Responsável",
      INSCESTCLI: "Inscrição Estadual",
      insc_municipal: "Inscrição Municipal",
      suframa: "SUFRAMA",
      contribuinte_icms: "Contribuinte ICMS",
      DDDCLI: "DDD (Telefone)",
      E_MAIL: "E-mail",
      FONECLI: "Telefone",
      DDDCELULAR: "DDD (Celular)",
      CELULAR: "Celular",
      site: "Website",
      CEPCLI: "CEP",
      ENDCLI: "Endereço",
      NUMERO: "Número",
      BAIRROCLI: "Bairro",
      CIDADECLI: "Cidade",
      ESTADOCLI: "Estado",
      LIMITE: "Limite de Crédito",
      metas_vendas: "Metas de Vendas",
      potencial_vendas: "Potencial de Vendas",
      acoes_vendas: "Ações de Vendas",
      OBS: "Observações",
      CGCCLI: "CNPJ",
      CPFCLI: "CPF",
    };

    const requiredMap = {
      ...fieldRequirements.cliente,
    };

    const pessoa = String(clienteData.PESSOA || "").toUpperCase();
    if (pessoa === "J") {
      requiredMap.CPFCLI = false;
    }

    if (pessoa === "F") {
      requiredMap.CGCCLI = false;
    }

    const missingFields = Object.keys(requiredMap)
      .filter((fieldName) => Boolean(requiredMap[fieldName]))
      .filter((fieldName) => {
        const value = clienteData[fieldName];
        return value === null || value === undefined || String(value).trim() === "";
      });

    if (missingFields.length > 0) {
      const labels = missingFields.map(
        (fieldName) => clienteFieldLabels[fieldName] || fieldName
      );
      toast.error(`Preencha os campos obrigatórios: ${labels.join(", ")}`);
      return;
    }
    
    setLoading(true);
    try {
      if (clienteId && clienteId !== "novo") {
        await api.put(`/clientes/${clienteId}`, clienteData);
        toast.success("Cliente atualizado com sucesso");
        fetchCliente();
      } else {
        const payload = {
          ...clienteData,
          preselectedContactId: preselectedContactId || undefined
        };

        const { data } = await api.post("/clientes", payload);
        toast.success("Cliente criado com sucesso");

        await requestFollowUpPrompt(i18n.t("followUpPrompt.sources.registerCliente"), {
          cliente: data || null,
          clienteId: data?.CODCLI || null,
          contatoId: data?.CODCLI ? preselectedContactId || null : null,
        });

        // Redireciona para a página de edição do cliente recém-criado
        // O useEffect vai carregar os dados automaticamente
        history.push(`/clientes/${data.CODCLI}`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isNewCliente = clienteId === "novo";

  return (
    <MainContainer>
      <MainHeader>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => history.push("/clientes")}>
            <ArrowBackIcon />
          </IconButton>
          <Title>
            {isNewCliente
              ? "Novo Cliente"
              : `Cliente: ${clienteData.NOMECLI || "..."}`}
          </Title>
        </Box>
        <MainHeaderButtonsWrapper>
          {!isNewCliente && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => history.push(`/clientes/${clienteId}/resumo`)}
              startIcon={<DescriptionIcon />}
              style={{ marginRight: 8 }}
            >
              Ver Resumo
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveCliente}
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? "Salvando..." : "Salvar"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Paper className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dados Gerais" />
            <Tab label="Endereços" disabled={isNewCliente} />
            <Tab label="Contatos" disabled={isNewCliente} />
            <Tab label="Histórico de Relacionamento" disabled={isNewCliente} />
            <Tab label="Negociações" disabled={isNewCliente} />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <ClienteDadosGerais
            clienteData={clienteData}
            setClienteData={setClienteData}
            loading={loading}
            fieldRequirements={fieldRequirements.cliente}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {!isNewCliente && <ClienteEnderecos clienteId={clienteId} />}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {!isNewCliente && (
            <ClienteContatos
              clienteId={clienteId}
              fieldRequirements={fieldRequirements.contato}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {!isNewCliente && <ClienteHistorico clienteId={clienteId} />}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {!isNewCliente && <ClienteNegociacoes clienteId={clienteId} />}
        </TabPanel>
      </Paper>
    </MainContainer>
  );
};

export default ClienteDetalhes;
